import { Injectable, OnDestroy } from '@angular/core';
import { HubConnection, HubConnectionState } from '@aspnet/signalr';
import { SignalRAspNetCoreHelper } from '@shared/helpers/SignalRAspNetCoreHelper';
import { GatewayPaymentDataDto, Transaction, TransactionDto } from '@shared/service-proxies/service-proxies';
import { Observable, Subject, ReplaySubject, BehaviorSubject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ReversalNotificationStatusMessageDto } from '../models/reversal-notification-status-message';


@Injectable()
export class PrinterService implements OnDestroy {
  private hubConnection: HubConnection;
  // Usar ReplaySubject con buffer de 1 para no perder el último evento
  private updateInvoiceSubject = new ReplaySubject<boolean>(1);
  private sendNumberSubject = new ReplaySubject<any>(1);
  private updatePrintersSubject = new ReplaySubject<boolean>(1);
  private paymentReceivedSubject = new ReplaySubject<boolean>(1);
  private isManuallyDisconnected: boolean = false;
  private isConnecting: boolean = false; // Bandera para evitar conexiones duplicadas
  private gatewayPaymentReceivedSubject = new ReplaySubject<GatewayPaymentDataDto>(1);
  private gatewayPaymentReversalSubject = new ReplaySubject<TransactionDto>(1);
  private gatewayPaymentReversalNotificationSubject = new ReplaySubject<ReversalNotificationStatusMessageDto>(1);
  
  // Configuración de reconexión con backoff exponencial
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 15;
  private baseReconnectInterval: number = 1000; // 1 segundo base
  
  // Heartbeat para detectar desconexiones silenciosas
  private connectionCheckInterval: any;
  private connectionCheckIntervalMs: number = 30000; // 30 segundos
  
  // Subject para notificar cuando la conexión está lista
  private connectionReadySubject = new BehaviorSubject<boolean>(false);
  connectionReady$ = this.connectionReadySubject.asObservable();

  updateInvoice$ = this.updateInvoiceSubject.asObservable();
  sendNumber$ = this.sendNumberSubject.asObservable();
  updatePrinters$ = this.updatePrintersSubject.asObservable();
  paymentReceived$ = this.paymentReceivedSubject.asObservable();
  gatewayPaymentReceived$ = this.gatewayPaymentReceivedSubject.asObservable();
  gatewayPaymentReversal$ = this.gatewayPaymentReversalSubject.asObservable();
  gatewayPaymentReversalNotification$ = this.gatewayPaymentReversalNotificationSubject.asObservable();

  constructor() {
    this.setupPrinterHub();
  }

  /** Retorna una promesa que se resuelve cuando la conexión está lista 
   * @param timeoutMs Tiempo máximo de espera en milisegundos (por defecto 30 segundos)
   */
  waitForConnection(timeoutMs: number = 30000): Promise<void> {
    // Si ya está conectado, resolver inmediatamente
    if (this.connectionReadySubject.value) {
      return Promise.resolve();
    }
    
    // Si no está conectando y no está conectado, iniciar conexión
    if (!this.isConnecting && !this.isConnected()) {
      this.ensureConnected();
    }
    
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        subscription.unsubscribe();
        reject(new Error('Timeout esperando conexión SignalR en PrinterService'));
      }, timeoutMs);
      
      const subscription = this.connectionReady$.pipe(
        filter(ready => ready),
        take(1)
      ).subscribe(() => {
        clearTimeout(timeoutId);
        subscription.unsubscribe();
        resolve();
      });
    });
  }

  /** Verifica si la conexión está activa */
  private isConnected(): boolean {
    return this.hubConnection && this.hubConnection.state === HubConnectionState.Connected;
  }

  private ensureConnected(): void {
    // No reconectar si ya está conectado, conectando, o desconectado manualmente
    if (this.isConnected() || this.isConnecting || this.isManuallyDisconnected) {
      return;
    }
    this.setupPrinterHub();
  }

  /** Fuerza la reconexión solo si es necesario */
  forceReconnect(): void {
    this.isManuallyDisconnected = false;
    this.reconnectAttempts = 0; // Resetear contador al forzar reconexión
    
    // Si ya está conectado o conectando, no hacer nada
    if (this.isConnected()) {
      console.log('PrinterService: Ya conectado, no es necesario reconectar.');
      return;
    }
    
    if (this.isConnecting) {
      console.log('PrinterService: Ya hay una conexión en proceso.');
      return;
    }
    
    this.setupPrinterHub();
  }

  private setupPrinterHub(): void {
    // Evitar conexiones duplicadas
    if (this.isConnecting) {
      console.log('PrinterService: Conexión ya en proceso, ignorando llamada duplicada.');
      return;
    }
    
    if (this.isConnected()) {
      console.log('PrinterService: Ya conectado, no es necesario reconectar.');
      this.connectionReadySubject.next(true);
      return;
    }
    
    this.isConnecting = true;
    this.isManuallyDisconnected = false;
    
    SignalRAspNetCoreHelper.initSignalR(() => {
        console.log('SignalR inicializado desde PrinterService.');
        abp.signalr.startConnection(abp.appPath + 'signalr-printerHub', (connection) => {
            console.log('Configurando conexión SignalR en PrinterService.'); 
            this.hubConnection = connection;

            this.registerOnServerEvents(connection);
            connection.onclose(() => {
                console.warn('Conexión SignalR cerrada en PrinterService.');
                this.isConnecting = false;
                this.connectionReadySubject.next(false);
                if (!this.isManuallyDisconnected) {
                    this.reconnect();
                }
            });
        })
        .then(() => {
            console.log('Conexión SignalR establecida en PrinterService.');
            this.isConnecting = false;
            this.reconnectAttempts = 0; // Resetear contador de reconexión
            this.connectionReadySubject.next(true);
            this.startConnectionCheck(); // Iniciar heartbeat
            abp.event.trigger('printerHub.connected');
        })
        .catch((err) => {
            console.error('Error al conectar SignalR desde PrinterService:', err);
            this.isConnecting = false;
            this.connectionReadySubject.next(false);
            this.reconnect();
        });
    });
}

  private reconnect(): void {
    // No reconectar si está desconectado manualmente o ya conectando
    if (this.isManuallyDisconnected || this.isConnecting) {
      return;
    }
    
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('PrinterService: Máximo de intentos de reconexión alcanzado (' + this.maxReconnectAttempts + ')');
      return;
    }
    
    // Backoff exponencial con jitter: evita que todos los clientes reconecten al mismo tiempo
    const exponentialDelay = Math.min(
      this.baseReconnectInterval * Math.pow(2, this.reconnectAttempts),
      30000 // máximo 30 segundos
    );
    const jitter = Math.random() * 1000; // añadir 0-1 segundo de jitter
    const delay = exponentialDelay + jitter;
    
    this.reconnectAttempts++;
    console.log(`PrinterService: Reconexión intento ${this.reconnectAttempts}/${this.maxReconnectAttempts} en ${Math.round(delay)}ms...`);
    
    setTimeout(() => this.setupPrinterHub(), delay);
  }

  /** Inicia verificación periódica de conexión (heartbeat) */
  private startConnectionCheck(): void {
    this.stopConnectionCheck(); // Limpiar cualquier intervalo previo
    this.connectionCheckInterval = setInterval(() => {
      if (!this.isConnected() && !this.isManuallyDisconnected && !this.isConnecting) {
        console.warn('PrinterService: Conexión perdida detectada por heartbeat, reconectando...');
        this.reconnect();
      }
    }, this.connectionCheckIntervalMs);
  }

  /** Detiene la verificación periódica de conexión */
  private stopConnectionCheck(): void {
    if (this.connectionCheckInterval) {
      clearInterval(this.connectionCheckInterval);
      this.connectionCheckInterval = null;
    }
  }

  private registerOnServerEvents( connection: any): void {
    connection.on('updateInvoices', (message: boolean) => {
      this.updateInvoiceSubject.next(message);
    });

    connection.on('sendNumber', (number: any) => {
      this.sendNumberSubject.next(number);
    });

    connection.on('updatePrinters', (message: boolean) => {
      this.updatePrintersSubject.next(message);
    });

    connection.on('paymentReceived', (message: boolean) => {
      this.paymentReceivedSubject.next(message);
    });

    connection.on('gatewayPaymentReceived', (paymentData : GatewayPaymentDataDto) => {
      this.gatewayPaymentReceivedSubject.next(paymentData);
    });

    connection.on('gatewayPaymentReversalReceived', (transaction: TransactionDto) => {
      this.gatewayPaymentReversalSubject.next(transaction);
    });

    connection.on('gatewayPaymentReversalProcessNotification', (notificationInfo: ReversalNotificationStatusMessageDto) => {
      this.gatewayPaymentReversalNotificationSubject.next(notificationInfo);
    });
  }

  disconnect(): void {
    this.stopConnectionCheck(); // Detener heartbeat
    this.isManuallyDisconnected = true; // Marca la desconexión manual
    this.reconnectAttempts = 0; // Resetear contador
    if (this.hubConnection && this.hubConnection.state !== HubConnectionState.Disconnected) {
      this.hubConnection.stop()
        .then(() => {
          console.log('Desconectado del hub de SignalR');
        })
        .catch(err => console.error('Error al desconectar la conexión de SignalR: ', err));
    }
  }

  ngOnDestroy(): void {
    this.stopConnectionCheck(); // Asegurar limpieza del heartbeat
    this.disconnect(); // Llama a desconectar al destruir el servicio
  }

  onUpdateInvoices(): Observable<boolean> {
    this.ensureConnected(); // Asegurarse de que está conectado
    return this.updateInvoice$;
  }

  onSendNumber(): Observable<any> {
    this.ensureConnected(); // Asegurarse de que está conectado
    return this.sendNumber$;
  }

  onUpdatePrinters(): Observable<boolean> {
    this.ensureConnected(); // Asegurarse de que está conectado
    return this.updatePrinters$;
  }

  onPaymentReceived(): Observable<boolean> {
    this.ensureConnected(); // Asegurarse de que está conectado
    return this.paymentReceived$;
  }

  onGatewayPaymentReceived(): Observable<GatewayPaymentDataDto> {
    this.ensureConnected();
    console.log("GatewayPaymentReceived",this.gatewayPaymentReceived$);
    return this.gatewayPaymentReceived$
  }

  onGatewayPaymentReversalReceived(): Observable<TransactionDto> {
    this.ensureConnected();
    return this.gatewayPaymentReversal$;
  }

  onGatewayPaymentReversalNotification(): Observable<ReversalNotificationStatusMessageDto> {
    this.ensureConnected();
    return this.gatewayPaymentReversalNotification$;
  }
}
